import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { syncQueue } from '../services/syncQueue';
import { ordersAPI } from '../services/api';

const OfflineContext = createContext(null);

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline debe usarse dentro de OfflineProvider');
  }
  return context;
};

export const OfflineProvider = ({ children }) => {
  const isOnline = useOnlineStatus();
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingOperations, setPendingOperations] = useState(syncQueue.getCount());
  const [syncErrors, setSyncErrors] = useState([]);

  // Actualizar contador de operaciones pendientes
  useEffect(() => {
    const unsubscribe = syncQueue.subscribe((queue) => {
      setPendingOperations(queue.length);
    });

    return unsubscribe;
  }, []);

  /**
   * Sincronizar una operación específica
   */
  const syncOperation = async (operation) => {
    try {
      console.log('🔄 Sincronizando operación:', operation);

      switch (operation.type) {
        case 'complete_order':
          await ordersAPI.complete(operation.payload.orderId);
          break;

        case 'unmark_order':
          await ordersAPI.unmark(operation.payload.orderId);
          break;

        case 'create_incident':
          // Aquí se podría agregar la lógica para crear incidencias
          // await incidentsAPI.create(operation.payload);
          console.log('Creación de incidencias aún no implementada en sincronización');
          break;

        default:
          console.warn('Tipo de operación desconocido:', operation.type);
          return false;
      }

      syncQueue.remove(operation.id);
      return true;
    } catch (error) {
      console.error('Error al sincronizar operación:', error);
      syncQueue.incrementRetries(operation.id);
      return false;
    }
  };

  /**
   * Sincronizar todas las operaciones pendientes
   */
  const syncAll = useCallback(async () => {
    if (!isOnline || isSyncing) {
      return;
    }

    const operations = syncQueue.getAll();
    if (operations.length === 0) {
      return;
    }

    console.log(`🔄 Iniciando sincronización de ${operations.length} operaciones...`);
    setIsSyncing(true);
    setSyncErrors([]);

    const errors = [];
    let successCount = 0;

    for (const operation of operations) {
      const success = await syncOperation(operation);
      if (success) {
        successCount++;
      } else {
        errors.push(operation);
      }
    }

    // Remover operaciones que fallaron demasiadas veces
    const failed = syncQueue.removeFailedOperations(5);
    if (failed.length > 0) {
      console.error('❌ Operaciones descartadas por exceso de reintentos:', failed);
    }

    setIsSyncing(false);
    setSyncErrors(errors);

    console.log(`✅ Sincronización completada: ${successCount}/${operations.length} exitosas`);

    return {
      success: successCount,
      failed: errors.length,
      total: operations.length
    };
  }, [isOnline, isSyncing]);

  /**
   * Sincronizar cuando se recupere la conexión
   */
  useEffect(() => {
    if (isOnline && pendingOperations > 0 && !isSyncing) {
      console.log('🌐 Conexión restablecida - Iniciando sincronización automática');
      // Pequeño delay para asegurar que la conexión esté estable
      const timer = setTimeout(() => {
        syncAll();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isOnline, pendingOperations, isSyncing, syncAll]);

  /**
   * Añadir una operación a la cola
   */
  const addToQueue = useCallback((type, payload) => {
    return syncQueue.add(type, payload);
  }, []);

  /**
   * Obtener estadísticas de la cola
   */
  const getQueueStats = useCallback(() => {
    return syncQueue.getStats();
  }, []);

  /**
   * Forzar sincronización manual
   */
  const forceSync = useCallback(async () => {
    if (!isOnline) {
      throw new Error('No hay conexión a Internet');
    }
    return await syncAll();
  }, [isOnline, syncAll]);

  const value = {
    isOnline,
    isSyncing,
    pendingOperations,
    syncErrors,
    addToQueue,
    syncAll,
    forceSync,
    getQueueStats
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};
